/* eslint-disable radix */
/* eslint-disable no-param-reassign */
import { findByFather } from "store/selectors/pageSelector";

const initialState = {
  components: []
};

/**
 * Add the elements array in the state.
 *
 * @param {Object} state current state
 * @param {Array} elementsArray Arrays of elements that need to be added.
 * @return {Object} resulting state
 */
const addElements = (state, elementsArray) => {
  const newState = [...state.components];
  elementsArray.forEach((element, index) => {
    const siblings = findByFather(newState, element.parent_id);
    if (element.component === "Column") {
      const position = siblings.length + 1;
      element.position = position;
    } else {
      const hasSamePos = siblings.filter(s => s.position === element.position)
        .length;
      if (hasSamePos) {
        siblings.forEach(sibling => {
          if (sibling.position >= element.position) {
            sibling.position += 1;
          }
        });
      }
    }
  });
  return {
    ...state,
    components: newState.concat(elementsArray)
  };
};

/**
 * Moves an element between parents.
 *
 * @param {Object} state current state
 * @param {Object} cords Object containing the source key (element which will be moved)
 * and destination key (new father for the element)
 */
const moveElement = (state, cords) => {
  const newState = state.components.map(component =>
    component.key === cords.source
      ? {
          ...component,
          parent_id: cords.destination,
          position: cords.position,
          props: {
            ...component.props,
            position: cords.position
          }
        }
      : component
  );
  const siblings = findByFather(newState, cords.destination);
  siblings.forEach(sibling => {
    if (sibling.key !== cords.source && sibling.position >= cords.position) {
      sibling.position = parseInt(sibling.position) + 1;
      sibling.props.position = sibling.props.position
        ? parseInt(sibling.props.position) + 1
        : sibling.position;
    }
  });
  return {
    ...state,
    components: newState
  };
};

/**
 * Will merge elements passed in the current state. Updating them
 * or adding new elements as needed.
 *
 * @param {Object} state current state
 * @param {Array} elements Elements array
 */
const updateElements = (state, elements) => {
  let newState = state.components.filter(oldEl => {
    return !elements.find(newEl => oldEl.key === newEl.key);
  });
  newState = newState.concat(elements);
  return {
    ...state,
    components: newState
  };
};

const removeElements = (state, payload) => {
  const fn = (arr, key) => {
    let ret = [key];
    const children = arr.filter(cmp => cmp.parent_id === key);
    children.forEach(cmp => {
      ret = ret.concat(fn(arr, cmp.key));
    });
    return ret;
  };

  const toBeRemoved = fn(state.components, payload);
  return {
    ...state,
    components: state.components.filter(cmp => !toBeRemoved.includes(cmp.key))
  };
};

const page = (state = initialState, action) => {
  switch (action.type) {
    case "SET_PAGE":
      return {
        ...state,
        ...action.payload
      };
    case "SET_PAGE_CONTENT":
      return {
        ...state,
        page_content: {
          ...state.page_content,
          ...action.payload
        }
      };
    case "ADD_COLUMN": {
      const { columns, newColumn } = action.payload;
      const newComponents = state.components.concat([newColumn]).map(cmp => {
        const t = columns.filter(i => i.key === cmp.key)[0];
        return t || cmp;
      });
      return {
        ...state,
        components: newComponents
      };
    }
    case "ADD_ELEMENTS":
      return addElements(state, action.payload);
    case "DELETE_ELEMENT":
      return removeElements(state, action.payload);
    case "DELETE_COLUMN": {
      const { column, newColumns } = action.payload;
      const newState = state.components
        .filter(cmp => {
          return (
            cmp.key !== column &&
            (cmp.parent_id === null || cmp.parent_id !== column)
          );
        })
        .map(cmp => {
          const newColumn = newColumns.find(i => i.key === cmp.key);
          return newColumn || cmp;
        });
      return {
        ...state,
        components: newState
      };
    }
    case "CLEAR_COLUMN":
      return {
        ...state,
        components: state.components.filter(
          cmp => cmp.parent_id !== action.payload
        )
      };
    case "CLEAR_SECTION": {
      const newComponents = state.components
        .filter(cmp => cmp.parent_id !== action.payload.key)
        .concat(action.payload.column);
      return {
        ...state,
        components: newComponents
      };
    }
    case "MOVE_ELEMENT":
      return moveElement(state, action.payload);
    case "DUPLICATE_COLUMN": {
      const { columns, newColumn, elements = [] } = action.payload;
      const newComponents = state.components
        .concat([newColumn, ...elements])
        .map(cmp => {
          const t = columns.filter(i => i.key === cmp.key)[0];
          return t || cmp;
        });
      return {
        ...state,
        components: newComponents
      };
    }
    case "DUPLICATE_SECTION": {
      const { sections, newSection, elements = [] } = action.payload;
      const newComponents = state.components
        .concat([newSection, ...elements])
        .map(cmp => {
          const t = sections.filter(i => i.key === cmp.key)[0];
          return t || cmp;
        });
      return {
        ...state,
        components: newComponents
      };
    }
    case "UPDATE_ELEMENTS":
      return updateElements(state, action.payload);
    case "UPDATE_ELEMENT": {
      const newComponents = state.components.map(component =>
        component.key === action.payload.key ? action.payload : component
      );
      return {
        ...state,
        components: newComponents
      };
    }
    default:
      return state;
  }
};

export default page;
